import { useEffect, useMemo, useRef, useState } from 'react';
import {
  COUNTRY_CODES,
  DEFAULT_IDENTITY,
  FirstName,
  GENDERS,
  Identity,
  ORIGIN_HINTS,
  PRONOUNS,
  ORIGINS,
  STARTING_SITUATIONS,
  SITUATION_HINTS,
  HOME_BY_SITUATION,
  DEFAULT_HOUSES,
  homeKindOf,
  UK_HOME_REGIONS,
  formatPence,
  type Appearance,
  type CharacterOptionsReply,
  type LifeStory,
  type MyLifeReply,
  type Origin,
  type StartingSituation,
  type UkHomeRegion,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { toPreviewCharacter } from '@london-life/game/appearance';
import { api } from '../lib/api';
import { AvatarPreview } from '../world';
import { BodyFields, Choices, StyleFields } from './look-fields';
import { actionKey } from '../lib/format';
import { useCatalogue } from '../lib/content';

const STEPS = ['You', 'Your story', 'Your look', 'Your name'] as const;
const countryNames = () => {
  const names = new Intl.DisplayNames(['en-GB'], { type: 'region' });
  return COUNTRY_CODES.filter((code) => code !== 'GB')
    .map((code) => ({ code, name: names.of(code) ?? code }))
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
};

const DEFAULT_LOOK: Appearance = {
  frame: 'feminine',
  build: 'average',
  skinTone: 6,
  hair: 'box_braids',
  hairColour: 'black',
  facialHair: null,
  outfit: 'casual',
  accessories: [],
};

// The character creator: body and identity beside the preview, then life story, look and name. The server checks every choice and pays the starting
// money; this screen only offers what the catalogue allows.
export function LifeCreator({
  catalogue,
  onStarted,
  onClose,
}: {
  catalogue: CharacterOptionsReply;
  onStarted: (reply: MyLifeReply) => void;
  onClose: () => void;
}) {
  // the homes (content): the first home the starting situation gives
  const houses = useCatalogue()?.houses ?? DEFAULT_HOUSES;
  const [step, setStepValue] = useState(0);
  // the furthest step reached: earlier steps can be revisited from the step list at any time
  const [reached, setReached] = useState(0);
  const setStep = (next: number) => {
    setStepValue(next);
    setReached((furthest) => Math.max(furthest, next));
  };
  // each step starts at its top (on phones the whole creator scrolls)
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => {
    body.current?.scrollTo?.({ top: 0 });
    body.current?.querySelector('.life-panel')?.scrollTo?.({ top: 0 });
  }, [step]);
  const [identity, setIdentity] = useState<Identity>(DEFAULT_IDENTITY);
  const [heritage, setHeritage] = useState('');
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [homeRegion, setHomeRegion] = useState<UkHomeRegion | ''>('');
  const [country, setCountry] = useState('');
  const [situation, setSituation] = useState<StartingSituation | null>(null);
  const [look, setLook] = useState<Appearance>(DEFAULT_LOOK);
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  // one key for this attempt: a double click or a retry after a network error starts one life only
  const [key] = useState(() => actionKey('life'));
  const countries = useMemo(() => countryNames(), []);

  const story: LifeStory | null =
    !origin || !situation
      ? null
      : origin === 'uk_born'
        ? homeRegion
          ? { origin, homeRegion, heritage: heritage || null, situation }
          : null
        : country
          ? { origin, country, situation }
          : null;
  const name = FirstName.safeParse(firstName);
  const who = Identity.safeParse(identity);
  const ready = [who.success, Boolean(story), true, name.success];

  const start = async () => {
    if (!story || !name.success) return;
    setBusy(true);
    setFailure(null);
    try {
      if (!who.success) return setStep(0);
      onStarted(await api.life.start({ firstName: name.data, identity: who.data, story, appearance: look }, key));
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="life-creator" role="dialog" aria-modal="true" aria-labelledby="life-creator-title">
      <header>
        {step > 0 && (
          <button type="button" className="life-back" onClick={() => setStep(step - 1)} disabled={busy}>
            ‹ Back
          </button>
        )}
        <h2 id="life-creator-title">Create your character</h2>
        <ol className="life-steps">
          {STEPS.map((label, index) => (
            <li key={label}>
              <button
                type="button"
                aria-current={index === step ? 'step' : undefined}
                disabled={index > reached || busy}
                onClick={() => setStep(index)}
              >
                {label}
              </button>
            </li>
          ))}
        </ol>
        <button type="button" className="life-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="life-body" ref={body}>
        <div className="life-stage">
          <AvatarPreview character={toPreviewCharacter(look, firstName || 'You')} />
          <p className="life-stage-note">Preview. The detailed characters arrive with the new avatar kit.</p>
        </div>
        <div className="life-panel">
          {step === 0 && (
            <>
              <BodyFields look={look} onChange={setLook} options={catalogue.options} />
              <label className="life-select">
                Gender
                <select
                  value={identity.gender}
                  onChange={(event) => setIdentity({ ...identity, gender: event.target.value as Identity['gender'] })}
                >
                  {(Object.keys(GENDERS) as Identity['gender'][]).map((value) => (
                    <option key={value} value={value}>
                      {GENDERS[value]}
                    </option>
                  ))}
                </select>
              </label>
              <Choices
                label="Pronouns"
                value={identity.pronouns}
                options={(Object.keys(PRONOUNS) as Identity['pronouns'][]).map((value) => ({
                  value,
                  label: PRONOUNS[value],
                }))}
                onChange={(pronouns) => setIdentity({ ...identity, pronouns })}
              />
              <p className="life-hint">
                Your body, gender and pronouns are separate choices, and every hairstyle and outfit is open to you.
              </p>
            </>
          )}
          {step === 1 && (
            <>
              <Choices
                label="Where does your UK story start?"
                value={origin}
                options={(Object.keys(ORIGINS) as Origin[]).map((value) => ({
                  value,
                  label: ORIGINS[value],
                  hint: ORIGIN_HINTS[value],
                }))}
                onChange={setOrigin}
              />
              {origin === 'uk_born' && (
                <>
                  <label className="life-select">
                    Home region
                    <select value={homeRegion} onChange={(event) => setHomeRegion(event.target.value as UkHomeRegion)}>
                      <option value="">Choose where you grew up</option>
                      {(Object.keys(UK_HOME_REGIONS) as UkHomeRegion[]).map((region) => (
                        <option key={region} value={region}>
                          {UK_HOME_REGIONS[region]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="life-select">
                    Family heritage (optional)
                    <select value={heritage} onChange={(event) => setHeritage(event.target.value)}>
                      <option value="">None, or prefer not to say</option>
                      {countries.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              {origin && origin !== 'uk_born' && (
                <label className="life-select">
                  {origin === 'moved_as_child' ? 'Country you were born in' : 'Country you came from'}
                  <select value={country} onChange={(event) => setCountry(event.target.value)}>
                    <option value="">Choose a country</option>
                    {countries.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <Choices
                label="Your situation"
                value={situation}
                options={(Object.keys(STARTING_SITUATIONS) as StartingSituation[]).map((value) => ({
                  value,
                  label: STARTING_SITUATIONS[value],
                  hint: `${SITUATION_HINTS[value]} · starts with ${formatPence(catalogue.startingMoneyPence[value] ?? 0)}`,
                }))}
                onChange={setSituation}
              />
            </>
          )}
          {step === 2 && (
            <>
              <StyleFields look={look} onChange={setLook} options={catalogue.options} />
            </>
          )}
          {step === 3 && (
            <>
              <label className="life-select">
                First name
                <Input
                  autoComplete="given-name"
                  maxLength={20}
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  placeholder="What shall we call you?"
                />
              </label>
              {firstName && !name.success && <p className="life-hint">{name.error.issues[0]?.message}</p>}
              {story && (
                <p className="life-summary">
                  {ORIGINS[story.origin]}
                  {story.origin === 'uk_born'
                    ? `, from ${UK_HOME_REGIONS[story.homeRegion]}`
                    : `, from ${countries.find((item) => item.code === story.country)?.name ?? story.country}`}
                  {story.origin === 'uk_born' && story.heritage
                    ? ` (${countries.find((item) => item.code === story.heritage)?.name} heritage)`
                    : ''}
                  . {STARTING_SITUATIONS[story.situation]}. You start with{' '}
                  <strong>{formatPence(catalogue.startingMoneyPence[story.situation] ?? 0)}</strong> in a{' '}
                  {homeKindOf(houses, HOME_BY_SITUATION[story.situation]).label.toLowerCase()} in{' '}
                  {homeKindOf(houses, HOME_BY_SITUATION[story.situation]).area}.
                </p>
              )}
              <p className="life-hint">
                You can change your look later at barbers, salons and clothes shops in the city. A new life can be
                started once every 7 days.
              </p>
            </>
          )}
          {failure && <ErrorNotice error={failure} />}
          <div className="life-actions">
            {step > 0 && (
              <Button type="button" variant="outline" onClick={() => setStep(step - 1)} disabled={busy}>
                Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button type="button" onClick={() => setStep(step + 1)} disabled={!ready[step]}>
                Next
              </Button>
            ) : (
              <Button type="button" onClick={() => void start()} disabled={busy || !ready.every(Boolean)}>
                {busy ? 'Starting…' : 'Start my life'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
